import React, { useEffect, useState } from 'react'
import moment from 'moment';
import 'moment/locale/es';
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
  Spinner
} from 'reactstrap';
import { FaPencilAlt, FaSave, FaTrash, FaFileAlt, FaFileDownload } from 'react-icons/fa'
import swal from 'sweetalert2';

// @components
import { MultiStepForm } from '../components/ProgramaStepForm/MultiStepForm'
import PaginaPorDefecto from './Default'
import TarjetaContenido from 'components/TarjetaContenido/TarjetaContenido'
import BuscadorTablas from 'components/Buscador/BuscadorTabla'

// @constantes
import { CAMPOS_PROGRAMA, LABELS_PROGRAMA } from '../components/ProgramaStepForm/constantes'
import { FORMATO_FECHA_DEFECTO } from 'utils/date';

// @hooks
import { useToggle } from 'hooks/useToggle';
import { usePrograma } from 'hooks/usePrograma'
import { useDescargarArchivo } from 'utils/aws';
import { ModalVerInfoPrograma } from 'components/ModalVerInfoPrograma/ModalVerInfoPrograma';

moment.locale('es', null);
const fechaDefecto = moment().format(FORMATO_FECHA_DEFECTO)
const fechaVencimientoDefecto =
  moment(fechaDefecto)
  .add(1, 'days').format(FORMATO_FECHA_DEFECTO)

const valorInicial = {
  [CAMPOS_PROGRAMA.PROGRAMA_ACREDITADO]: false,
  [CAMPOS_PROGRAMA.ACUERDO_CREACION_PROGRAMA]: '',
  [CAMPOS_PROGRAMA.PERIODOS]: 1,
  [CAMPOS_PROGRAMA.CODIGO_SNIES]: 0,
  [CAMPOS_PROGRAMA.CREDITOS]: 1,
  [CAMPOS_PROGRAMA.RC_FECHA_REGISTRO]: fechaDefecto,
  [CAMPOS_PROGRAMA.RC_FECHA_VENCIMIENTO]: fechaVencimientoDefecto
}

export default function Programas_academicos() {
  /**
   * Stado
   */
  const { open, onToggle } = useToggle()
  const { open: abrirModalPrograma, onToggle: onTogglePrograma } = useToggle()
  const [buscar, setBuscar] = useState('')
  const [programaAcademico, setProgramaAcademico] = useState(valorInicial)
  const [errors, setErrors] = useState(null)

  /**
   * Efectos
   */
  useEffect(() => {
    setErrors(null)
  }, [programaAcademico])

  useEffect(() => {
    if (errors) {
      swal
        .fire({
          title: 'Upps',
          html: Object.entries(errors).map(([field, errors]) =>
            `<div style='display:flex; justify-content:center'> 
              <strong>${LABELS_PROGRAMA[field] ?? field}:</strong>
              <ul style='list-style-type: none; padding-inline-start: 10px;'>
                ${errors.map(
                  error => `<li>${error}</li>`)
                  .join()}
              </ul>
            </div>`
          ).join('\n'),	
          type: 'error',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Continuar',
        })
        .then(async ({ value }) => {
          if (value) {
            setErrors(null)
          }
        })
    }
  }, [errors])

  /**
   * Consultas
   */
  const {
    obtenerProgramas,
    handleCrearPrograma,
    handleBuscarPrograma,
    handleActualizarPrograma,
    handleEliminarPrograma,
  } = usePrograma()
  const { data: programas } = obtenerProgramas(buscar)

  /**
   * Utiles
   */
  const [
    descargarArchivo, { estaDescargando }
  ] = useDescargarArchivo({ setErrors })

  /**
   * Manejadores
   */
  const guardarProgramaAcademico = async (navigation) => {
    const resetForm = () => {
      setProgramaAcademico(valorInicial)
      if (navigation) {
        navigation.go('programa-escuela')
      }
    }

    if (programaAcademico.id) {
      await handleActualizarPrograma({
        values: programaAcademico,
        acciones: {
          resetForm,
          setErrors
        },
        id: programaAcademico.id,
      })
    } else {
      await handleCrearPrograma(programaAcademico, {
        resetForm,
        setErrors
      })
    }
    onToggle()
  }

  const manejadorCerrarModal = () => {
    setProgramaAcademico(valorInicial)
  }

  const onEditarPrograma = () => { 
    onTogglePrograma()
    onToggle()
  }

  return (
    <PaginaPorDefecto titulo='Programas Académicos'>
      <ModalVerInfoPrograma
        programa={programaAcademico}
        onToggle={onTogglePrograma}
        open={abrirModalPrograma}
        onEditarPrograma={onEditarPrograma}
      />
      <Modal
        isOpen={open}
        toggle={onToggle}
        size='lg'
        unmountOnClose
        onClosed={manejadorCerrarModal}
      >
        <ModalHeader toggle={onToggle}>
          {programaAcademico.id ? 'Editar' : 'Crear'} Programa
        </ModalHeader>
        <ModalBody>
          <MultiStepForm
            programaAcademico={programaAcademico}
            actualizarProgramaAcademico={setProgramaAcademico}
            guardarPrograma={guardarProgramaAcademico}
          />
        </ModalBody>
      </Modal>
      <TarjetaContenido
        titulo={<span>Listado de programas Académicos {(estaDescargando || !programas) && <Spinner color='primary' style={{ marginLeft: '1rem' }} />}</span>}
        component={
          <Button outline color='primary' onClick={onToggle}>
            <FaSave />
          </Button>
        }
      >
        <BuscadorTablas
          valor={buscar}
          onChange={setBuscar}
          onBuscar={handleBuscarPrograma}
        />
          <Table responsive>
            <thead>
              <tr>
                <th>{LABELS_PROGRAMA[CAMPOS_PROGRAMA.ESCUELA]}</th>
                <th>{LABELS_PROGRAMA[CAMPOS_PROGRAMA.NIVEL_FORMACION]}</th>
                <th>{LABELS_PROGRAMA[CAMPOS_PROGRAMA.NOMBRE_PROGRAMA_ACADEMICO]}</th>
                <th>{LABELS_PROGRAMA[CAMPOS_PROGRAMA.ACUERDO_CREACION_PROGRAMA]}</th>
                <th>{LABELS_PROGRAMA[CAMPOS_PROGRAMA.CODIGO_SNIES]}</th>
                <th>Registro calificado</th>
                <th>Documents</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {programas?.results.map((programa) => {
                const manejadorEditarPrograma = () => {
                  setProgramaAcademico(programa)
                  onToggle()
                }

                const manejadorVerPrograma = () => {
                  setProgramaAcademico(programa)
                  onTogglePrograma()
                }

                const manjadorEliminarPrograma = () =>
                  handleEliminarPrograma(programa)
                
                const manejadorClickDescargarRC = async () => {
                  await descargarArchivo(programa.rc_archivo_url)
                }
                
                const manejadorClickDescargarAC = async () => {
                  await descargarArchivo(programa.ac_archivo_url) 
                }
                
                return (
                <tr key={programa.id}>
                  <td>{programa.escuela__nombre}</td>
                  <td>{programa.nivel_formacion}</td>
                  <td>{programa.nombre}</td>
                  <td>{programa.acdp}</td>
                  <td>{programa.snies}</td>
                  <td>Resolución {
                    programa.rc_resolucion
                  } del {
                      moment(programa.rc_fecha_creacion, FORMATO_FECHA_DEFECTO)
                        .format('LL')
                      }</td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <span
                          style={{ marginRight: '10px' }}
                          title='Descargar registro de calificación'
                        >
                          <Button
                            color='primary'
                            onClick={manejadorClickDescargarRC}
                          >
                          <FaFileDownload/>
                          </Button>
                        </span>
                        {programa.programa_acreditado && (
                          <span title='Descargar Acuerdo de acreditación'>
                            <Button
                              color='secondary'
                              onClick={manejadorClickDescargarAC}
                            >
                            <FaFileDownload />
                            </Button>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className='d-flex'>
                    <Button
                      outline
                      color='primary'
                      size='sm'
                      className='mr-1'
                      onClick={manejadorVerPrograma}
                    >
                      <FaFileAlt />
                    </Button>
                    <Button
                      outline
                      color='primary'
                      size='sm'
                      className='mr-1'
                      onClick={manejadorEditarPrograma}
                    >
                      <FaPencilAlt />
                    </Button>
                    <Button
                      outline
                      color='danger'
                      size='sm'
                      onClick={manjadorEliminarPrograma}
                    >
                      <FaTrash />
                    </Button>
                    
                  </td>
                </tr>
              )})}
            </tbody>
          </Table>
      </TarjetaContenido>
    </PaginaPorDefecto>
  )
}
